import {
  ErrorDetailsDtoErrorCodeEnum,
  ResponseErrorDto,
  VehicleCategory,
} from '@interfaces/models';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { VehicleCountryCodes, Validators } from '@helpers/constant';
import { createVehicle } from '@api/vehicles/vehicles.service';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetCostCenters } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { compareLabels } from '@helpers/utils';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { z } from 'zod';

import { useVehicleAssignEmployees } from '../vehicle-assign-employees/use-vehicle-assign-employeess';

const vehicleFormSchema = (t: TFunction) =>
  z.object({
    registrationNumber: z
      .string()
      .trim()
      .min(4, { message: t('FORMS.MIN_CHARACTERS', { limit: 4 }) })
      .max(10, { message: t('FORMS.MAX_CHARACTERS', { limit: 10 }) })
      .regex(Validators.REGISTRATION_NUMBER, t('FORMS.INVALID_REGISTRATION_NUMBER')),
    name: z
      .string()
      .trim()
      .min(2, { message: t('FORMS.MIN_CHARACTERS', { limit: 2 }) })
      .max(20, {
        message: t('FORMS.MAX_CHARACTERS', { limit: 20 }),
      })
      .or(z.literal('')),
    costCenters: z.array(z.string()).min(1, { message: t('FORMS.REQUIRED') }),
    registrationCountry: z.string().min(1, { message: t('FORMS.REQUIRED') }),
    vehicleCategory: z.string().min(1, { message: t('FORMS.REQUIRED') }),
    assignedEmployees: z.array(z.string()).optional(),
  });

type VehicleFormData = z.infer<ReturnType<typeof vehicleFormSchema>>;

const DEFAULT_VALUES: VehicleFormData = {
  registrationCountry: 'PL',
  registrationNumber: '',
  vehicleCategory: VehicleCategory.CATEGORY1,
  costCenters: [],
  name: '',
  assignedEmployees: [],
};

export const useCreateVehicle = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    costCentersOptions: costCenterOptions,
    isFetching: isCostCenterLoading,
    automaticAssignmentCostCenter,
  } = useGetCostCenters();

  const form = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleFormSchema(t)),
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const vehicleCategoryOptions = Object.values(VehicleCategory)
    .map((value) => ({
      label: t(`VEHICLE_CATEGORY.${value}`),
      value,
    }))
    .sort(compareLabels);

  const { mutate: createVehicleMutation, isPending: isCreateVehiclePending } = useMutation({
    mutationFn: createVehicle,
    onSuccess: () => {
      form.reset(DEFAULT_VALUES);
      queryClient.invalidateQueries({ queryKey: ['vehicles-table'] });
      toast.success(t('VEHICLES.CREATE_VEHICLE_SUCCESS'));
      onSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      if (error.errorCode === ErrorDetailsDtoErrorCodeEnum.REGISTRATION_NUMBER_ALREADY_EXISTS) {
        form.setError('registrationNumber', {
          message: t('API_ERRORS.VEHICLE_REGISTRATION_NUMBER_NOT_UNIQUE'),
        });
        return;
      }
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const assignedEmployees = form.watch('assignedEmployees');
  const assignTable = useVehicleAssignEmployees(assignedEmployees || []);

  const createVehicleHandler = (data: VehicleFormData) => {
    createVehicleMutation({ ...data, vehicleCategory: data.vehicleCategory as VehicleCategory });
  };

  const onSubmit = form.handleSubmit(createVehicleHandler);

  const selectedCostCenters = form.watch('costCenters');

  useDeepCompareEffect(() => {
    if (!selectedCostCenters.length && automaticAssignmentCostCenter) {
      form.setValue('costCenters', [automaticAssignmentCostCenter.id]);
    }
  }, [automaticAssignmentCostCenter, selectedCostCenters]);

  return {
    isLoading: isCostCenterLoading,
    countryOptions: VehicleCountryCodes,
    isCreateVehiclePending,
    vehicleCategoryOptions,
    costCenterOptions,
    assignedEmployees,
    assignTable,
    onSubmit,
    form,
    t,
  };
};
